import { useReducedMotion } from 'motion/react'

/** `true` when the user prefers reduced motion (or while preference is unknown). */
export function usePrefersReducedMotion(): boolean {
  return useReducedMotion() ?? false
}

/** Fade + rise on enter; opacity-only when reduced motion is preferred. */
export function fadeUpProps(reduceMotion: boolean, delay = 0) {
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.2, delay },
    } as const
  }
  return {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay },
  } as const
}

/** Badge / label pop-in before headline. */
export function badgePopProps(reduceMotion: boolean) {
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.2 },
    } as const
  }
  return {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    transition: { delay: 0.1, duration: 0.35 },
  } as const
}

/** Carousel / tab panel — slides out left, enters from right. */
export function horizontalSlideProps(reduceMotion: boolean, soft = false) {
  const x = soft ? 50 : 80
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 },
    } as const
  }
  return {
    initial: { opacity: 0, x },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -x },
    transition: { duration: soft ? 0.3 : 0.4 },
  } as const
}

/** Multi-step wizard — step slides in from right, exits left. */
export function wizardStepProps(reduceMotion: boolean) {
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 },
    } as const
  }
  return {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
    transition: { duration: 0.35 },
  } as const
}

/** Mobile nav drawer — height expand/collapse. */
export function expandCollapseProps(reduceMotion: boolean) {
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.15 },
    } as const
  }
  return {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.25 },
  } as const
}

/** Leaderboard list row — slide in from left with stagger delay. */
export function listRowSlideProps(
  reduceMotion: boolean,
  index: number,
  delayMs = 30,
) {
  const delay = index * (delayMs / 1000)
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { delay, duration: 0.2 },
    } as const
  }
  return {
    initial: { opacity: 0, x: -10 },
    animate: { opacity: 1, x: 0 },
    transition: { delay, duration: 0.35 },
  } as const
}

/** Podium / top cards — fade up with slower stagger. */
export function podiumCardProps(reduceMotion: boolean, index: number) {
  const delay = index * 0.1
  return fadeUpProps(reduceMotion, delay)
}

/** Page enter: optional multi-section cascade (e.g. dashboard). */
export const pageEnterContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.02,
    },
  },
} as const

/** @deprecated Use `pageEnterContainer` */
export const dashboardEnterContainer = pageEnterContainer

export function pageEnterSection(reduceMotion: boolean) {
  if (reduceMotion) {
    return {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: { duration: 0.15, ease: 'easeOut' as const },
      },
    } as const
  }
  return {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
    },
  } as const
}

/** @deprecated Use `pageEnterSection` */
export const dashboardEnterSection = pageEnterSection

/** Success check — spring scale pop. */
export function springPopProps(reduceMotion: boolean) {
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.2 },
    } as const
  }
  return {
    initial: { scale: 0 },
    animate: { scale: 1 },
    transition: { type: 'spring' as const, duration: 0.5 },
  } as const
}
